import { useState, useEffect, useRef } from "react";
import { imageCache } from "@/lib/imageCache";

interface UseImagePreloaderProps {
  imageUrls: string[];
  enabled?: boolean;
}

export const useImagePreloader = ({ imageUrls, enabled = true }: UseImagePreloaderProps) => {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  // Use useRef to keep Map reference stable
  const preloadedImagesRef = useState<Map<string, string>>(() => new Map())[0];
  const blobUrlsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || imageUrls.length === 0) {
      setImagesLoaded(true);
      return;
    }

    let mounted = true;
    let loadedImages = 0;

    const preloadImage = async (url: string): Promise<void> => {
      try {
        // Check IndexedDB cache first
        const cachedBlob = await imageCache.get(url);
        if (cachedBlob && mounted) {
          const blobUrl = URL.createObjectURL(cachedBlob);
          preloadedImagesRef.set(url, blobUrl);
          blobUrlsRef.current.add(blobUrl);
          loadedImages++;
          setLoadedCount(loadedImages);
          return;
        }

        // Fetch from network
        const response = await fetch(url);
        const blob = await response.blob();

        if (mounted) {
          // Store in IndexedDB cache
          await imageCache.set(url, blob);

          // Create blob URL for instant rendering
          const blobUrl = URL.createObjectURL(blob);
          preloadedImagesRef.set(url, blobUrl);
          blobUrlsRef.current.add(blobUrl);
          
          loadedImages++;
          setLoadedCount(loadedImages);
        }
      } catch (error) {
        console.error(`Error preloading image ${url}:`, error);
        if (mounted) {
          loadedImages++;
          setLoadedCount(loadedImages);
        }
      }
    };

    const preloadAllImages = async () => {
      try {
        await Promise.all(imageUrls.map(url => preloadImage(url)));
        if (mounted) {
          setImagesLoaded(true);
        }
      } catch (error) {
        console.error("Error preloading images:", error);
        if (mounted) {
          setImagesLoaded(true);
        }
      }
    };

    preloadAllImages();

    return () => {
      mounted = false;
    };
  }, [imageUrls, enabled]);

  // Cleanup blob URLs only on component unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach(blobUrl => URL.revokeObjectURL(blobUrl));
      blobUrlsRef.current.clear();
    };
  }, []);

  return {
    imagesLoaded,
    loadedCount,
    totalImages: imageUrls.length,
    progress: imageUrls.length > 0 ? (loadedCount / imageUrls.length) * 100 : 100,
    preloadedImages: preloadedImagesRef,
  };
};
